import _defineProperty from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as R from 'ramda';
import { commonSettings } from 'src/constants';
import { THERAPY_CATEGORY, TRANSACTION_TYPE_IDS } from 'src/constants/order';
import { Categories, OrderType, PaymentType } from 'src/types/order';
import { getUserName } from '..';
export var sumBy = function sumBy(key, array) {
  return array.reduce(function (acc, item) {
    return acc + item[key];
  }, 0);
};
/**
 * For those orders with offset order items, when we add first payment to the order,
 * the server would create a virtual payment with transaction_type = 2 additionally.
 * And it's for accounting report only. So we need to filter it out.
 */

export var stripOffsetPayment = function stripOffsetPayment() {
  var payments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return payments.filter(function (_ref) {
    var transaction_type_id = _ref.transaction_type;
    return TRANSACTION_TYPE_IDS.has(transaction_type_id);
  });
};
export var getUserOptions = function getUserOptions(users) {
  return users.map(function (user) {
    return {
      label: getUserName(user),
      value: user.id,
      id: user.id
    };
  });
};
export var getIsDoctorRequired = function getIsDoctorRequired(orderForm) {
  return orderForm.order_type === OrderType.MEDICAL_PRESCRIPTION;
};
export var getOrderItemsByCategory = function getOrderItemsByCategory(orderForm, category) {
  return R.props(typeof category === 'string' ? [category] : category, orderForm).flat().filter(function (_ref2) {
    var _destroy = _ref2._destroy;
    return !_destroy;
  });
};
export var canPayInInstallments = function canPayInInstallments(orderForm) {
  return getOrderItemsByCategory(orderForm, Categories.THERAPY).length > 0;
};
export var isToRefund = function isToRefund(transaction_type) {
  return transaction_type === commonSettings.TRANSACTION_TYPE.refund.key;
};
/**
 * get product accounting category from order item
 * NOTICE: `product_categories` might be null, especially in legacy product data.
 * And there's no i18n for it.
 * @param item order item
 */

export var getProductAccountingCategory = function getProductAccountingCategory(item) {
  return R.pipe(R.pathOr([], ['products', 'product_categories']), R.find(function (_ref3) {
    var group = _ref3.group;
    return group === '會計類別';
  }), R.propOr('-', 'name'))(item);
};
/**
 * turn order items except offset into item payments, and merge with offset items, sales allowances, existing item payments, and draft
 */

export var initializeItemPayments = function initializeItemPayments(orderForm) {
  var allOrderItems = getOrderItemsByCategory(orderForm, Object.values(Categories).filter(function (category) {
    return category !== Categories.OFFSET;
  }));
  var offsetItems = getOrderItemsByCategory(orderForm, Categories.OFFSET);
  var totalOffsetAmount = sumBy('discounted_amount', offsetItems);
  var salesAllowanceMap = R.pipe(R.path(['bill', 'sales_allowances']), R.chain(R.prop('items')), R.groupBy(R.prop('_id')), R.mapObjIndexed(function (arr) {
    return sumBy('amount', arr);
  }))(orderForm);
  var itemPaymentMap = R.pipe(R.pathOr([], ['bill', 'payments']), R.chain(R.propOr([], 'item_payments')), R.groupBy(R.prop('item_id')), R.mapObjIndexed(function (arr) {
    return sumBy('amount', arr);
  }))(orderForm);
  var payment_draft = R.pathOr({}, ['bill', 'payment_draft'], orderForm);
  var isRefund = isToRefund(payment_draft.transaction_type);
  var draftItemsMap = R.pipe(R.propOr([], 'item_payments'), R.indexBy(R.prop('item_id')), R.mapObjIndexed(function (item) {
    return _objectSpread(_objectSpread({}, item), {}, {
      isRefund: isRefund
    });
  }))(payment_draft);
  var isOffset = false;
  return allOrderItems.map(function (item) {
    var isTherapy = getIsTherapy(item);
    var price_after_discount_and_offset = item.discounted_amount; // add offset amount to the first therapy item
    // Theoretically, there should be only one therapy item when offset items exist

    if (isTherapy && !isOffset) {
      price_after_discount_and_offset += totalOffsetAmount;
      isOffset = true;
    }

    return _objectSpread(_objectSpread({}, item), {}, {
      accounting_category: getProductAccountingCategory(item),
      sales_allowance_amount: salesAllowanceMap[item._id] || 0,
      received_amount: itemPaymentMap[item._id] || 0,
      price_after_discount_and_offset: price_after_discount_and_offset,
      payment_draft: draftItemsMap[item._id],
      amount: 0
    });
  });
};

var getIsTherapy = function getIsTherapy(orderItem) {
  return orderItem.productable_type === THERAPY_CATEGORY.productable_type;
};
/**
 * get default amount for each item payment
 */


export var getItemPaymentDefaultAmount = function getItemPaymentDefaultAmount(orderItem, isRefund, totalOffsetAmount, payment_type) {
  if (isRefund) {
    return 0;
  }

  var received_amount = orderItem.received_amount,
      discounted_amount = orderItem.discounted_amount,
      sales_allowance_amount = orderItem.sales_allowance_amount;
  var isTherapy = getIsTherapy(orderItem);
  var amount = 0; // non-therapy items can only pay in full

  if (!isTherapy) {
    amount = discounted_amount - sales_allowance_amount - received_amount;
  } // first installment of therapy
  else if (payment_type === PaymentType.PAY_IN_INSTALLMENTS && received_amount === 0) {
    amount = discounted_amount / 2 - sales_allowance_amount - Math.abs(totalOffsetAmount);
  } // the rest installments or therapy item pay in full
  else {
    amount = discounted_amount - sales_allowance_amount - Math.abs(totalOffsetAmount) - received_amount;
  }

  return Math.max(amount, 0);
};
/**
 * reset itemPayments
 */

export var resetItemPayments = function resetItemPayments(itemPayments, isRefund, totalOffsetAmount, payment_type) {
  var ignoreDraft = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  return itemPayments.map(function (itemPayment) {
    var _payment_draft$amount;

    var payment_draft = itemPayment.payment_draft;
    var hasDraft = Boolean(!ignoreDraft && payment_draft && payment_draft.isRefund === isRefund);
    var amount = hasDraft ? (_payment_draft$amount = payment_draft.amount) !== null && _payment_draft$amount !== void 0 ? _payment_draft$amount : 0 : getItemPaymentDefaultAmount(itemPayment, isRefund, totalOffsetAmount, payment_type); // item is editable when it's receivable amount in not 0

    var editable = itemPayment.discounted_amount !== 0 && itemPayment.price_after_discount_and_offset !== 0 && (isRefund ? true : itemPayment.price_after_discount_and_offset - itemPayment.sales_allowance_amount > itemPayment.received_amount); // item is active when it's editable or has draft, default to false when it's refund, otherwise true

    var _active = editable && (hasDraft ? payment_draft._active : !isRefund);

    return _objectSpread(_objectSpread({}, itemPayment), {}, {
      amount: amount,
      _active: _active,
      editable: editable
    });
  });
};
export var sortItemPayments = function sortItemPayments(itemPayments) {
  return itemPayments.sort(function (a, b) {
    if (a.discounted_amount === 0 && b.discounted_amount !== 0) {
      return 1;
    } else if (a.discounted_amount !== 0 && b.discounted_amount === 0) {
      return -1;
    } else {
      return 0;
    }
  });
};
export var sumItemPaymentsAmount = function sumItemPaymentsAmount(item_payments) {
  return item_payments.reduce(function (acc, item) {
    return item._active ? acc + item.amount : acc;
  }, 0);
};
/**
 * mapping payment_type id to key
 * @param data payment_type id
 */

export var getPaymentType = function getPaymentType(data) {
  var _commonSettings$PAYME;

  return (_commonSettings$PAYME = commonSettings.PAYMENT_TYPES.find(function (_ref4) {
    var id = _ref4.id;
    return id === data;
  })) === null || _commonSettings$PAYME === void 0 ? void 0 : _commonSettings$PAYME.key;
};
export var checkIsInvoiceRequired = function checkIsInvoiceRequired(orderItem) {
  var invoice_accounting_categories = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var accounting_category = getProductAccountingCategory(orderItem);
  return invoice_accounting_categories.includes(accounting_category);
};
export var getInvoiceOrReceiptAmount = function getInvoiceOrReceiptAmount(orderItems) {
  var offsetItems = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var salesAllowances = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  var invoice_accounting_categories = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  var isInvoice = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var totalOffsetAmount = sumBy('discounted_amount', offsetItems);
  var salesAllowanceItemMap = R.pipe(R.chain(R.prop('items')), R.groupBy(R.prop('_id')), R.mapObjIndexed(function (items) {
    return sumBy('amount', items);
  }))(salesAllowances);
  var result = orderItems.filter(function (item) {
    return isInvoice === checkIsInvoiceRequired(item, invoice_accounting_categories);
  }).reduce(function (acc, item) {
    var amount = item.discounted_amount;

    if (R.has(item._id, salesAllowanceItemMap)) {
      amount -= salesAllowanceItemMap[item._id];
    } // offset amount can only be added to the therapy item, and there should be only one therapy item in order when offset items exist


    if (getIsTherapy(item) && totalOffsetAmount < 0) {
      amount += totalOffsetAmount;
      amount = Math.max(amount, 0);
      totalOffsetAmount = 0;
    }

    return acc + amount;
  }, 0);
  return result;
};